/**
 * Firestore access for `skidaCrmPartners`.
 *
 * Small collection, loaded whole and kept live — the same shape as the posts
 * repository next door, for the same reason: a partner list is tens of rows, and
 * paging it would cost more in complexity than it saves.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'
import { strip } from '@/shared/util/strip'
import {
  PARTNER_KINDS,
  PARTNER_STATUSES,
  type Partner,
  type PartnerKind,
  type PartnerStatus,
} from '@/types/partners'

const COLLECTION = 'skidaCrmPartners'

const str = (v: unknown) => (typeof v === 'string' ? v : '')

function mapPartner(d: QueryDocumentSnapshot<DocumentData>): Partner {
  const v = d.data()
  const kind = str(v.kind)
  const status = str(v.status)
  return {
    // Written by scripts/geocodeAll.ts and by nothing else, so it is passed
    // through as stored — precision included, which is the half that matters.
    // Spread in, never set to `undefined`.
    //
    // `geo: v.geo ?? undefined` looks equivalent and is not: it creates a
    // *present key with no value*, which is the one thing Firestore refuses.
    // Every partner without a geocoded point then became unsaveable —
    // "Unsupported field value: undefined (found in field geo)" — because the
    // modal hands the whole record back and the write spread it. `mapPurchases`
    // in the ownership repository already documents this exact trap.
    ...(v.geo ? { geo: v.geo as Partner['geo'] } : {}),
    id: d.id,
    name: str(v.name),
    // An unknown value falls back rather than rendering a blank cell, so a bad
    // import shows up as "Other" and not as an empty column nobody notices.
    kind: (PARTNER_KINDS as readonly string[]).includes(kind) ? (kind as PartnerKind) : 'Other',
    status: PARTNER_STATUSES.some((s) => s.key === status)
      ? (status as PartnerStatus)
      : 'talking',
    country: str(v.country),
    website: str(v.website),
    logoUrl: str(v.logoUrl),
    // Stored bare; a handle typed with the @ would never match what the
    // platform returns.
    instagram: str(v.instagram).replace(/^@/, ''),
    facebook: str(v.facebook).replace(/^@/, ''),
    contactName: str(v.contactName),
    contactRole: str(v.contactRole),
    email: str(v.email),
    phone: str(v.phone),
    arrangement: str(v.arrangement),
    campaignCode: str(v.campaignCode),
    notes: str(v.notes),
    owner: str(v.owner),
    linkedLeadId: str(v.linkedLeadId),
    createdAt: toIsoOrNull(v.createdAt),
    updatedAt: toIsoOrNull(v.updatedAt),
  }
}

export function subscribeToPartners(
  onData: (partners: Partner[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COLLECTION)),
    (snap) => {
      const rows = snap.docs.map(mapPartner)
      rows.sort((a, b) => a.name.localeCompare(b.name))
      onData(rows)
    },
    (e) => onError(e as Error),
  )
}

type PartnerInput = Omit<Partner, 'id' | 'createdAt' | 'updatedAt'>

export async function createPartner(input: PartnerInput): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...strip(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updatePartner(id: string, patch: Partial<PartnerInput>): Promise<void> {
  // `strip` as well as the fix above, deliberately. The mapper no longer creates
  // an undefined key, but this function takes whatever a caller hands it, and
  // the ownership repository has stripped every write for the same reason since
  // the day a purchase without a note made every holder unsaveable.
  await setDoc(
    doc(db, COLLECTION, id),
    { ...strip(patch), updatedAt: serverTimestamp() },
    { merge: true },
  )
}

export async function deletePartner(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}
